
import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface FloatingHelpButtonProps {
  scrollToId?: string;
  link?: string;
  tooltip?: string;
}

const FloatingHelpButton = ({ scrollToId, link, tooltip = "Get help with booking or questions about our tours" }: FloatingHelpButtonProps) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const { toast } = useToast();
  
  const handleClick = () => {
    if (scrollToId) {
      const element = document.getElementById(scrollToId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        
        // Highlight the element briefly
        element.classList.add('highlight-pulse');
        setTimeout(() => {
          element.classList.remove('highlight-pulse');
        }, 2000);
        
      } else {
        // Element not found, show toast error
        toast({
          title: "Section Not Found",
          description: "We couldn't find that section. Please try another link.",
          variant: "destructive",
        });
      }
    }
  };

  const buttonClass = cn(
    "fixed bottom-6 right-6 z-50 bg-[#800020] text-white hover:text-white p-3 rounded-full",
    "shadow-lg hover:bg-[#800020]/90 transition-all duration-300 flex items-center justify-center hover:scale-110"
  );
  
  const showTooltip = () => setTooltipVisible(true);
  const hideTooltip = () => setTooltipVisible(false);

  if (link) {
    return (
      <div className="relative" onMouseEnter={showTooltip} onMouseLeave={hideTooltip} onFocus={showTooltip} onBlur={hideTooltip}>
        <a 
          href={link}
          className={buttonClass}
          aria-label={tooltip}
        >
          <HelpCircle size={24} />
        </a>
        
        {tooltipVisible && (
          <div className="absolute bottom-16 right-0 bg-white text-primary px-3 py-2 rounded shadow-lg text-sm w-48 pointer-events-none">
            {tooltip}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white transform rotate-45"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" onMouseEnter={showTooltip} onMouseLeave={hideTooltip} onFocus={showTooltip} onBlur={hideTooltip}>
      <button
        onClick={handleClick}
        className={buttonClass}
        aria-label={tooltip}
      >
        <HelpCircle size={24} />
      </button>
      
      {tooltipVisible && (
        <div className="absolute bottom-16 right-0 bg-white text-primary px-3 py-2 rounded shadow-lg text-sm w-48 pointer-events-none">
          {tooltip}
          <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white transform rotate-45"></div>
        </div>
      )}
    </div>
  );
};

export default FloatingHelpButton;
